import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, Users, History, Receipt, Menu, Trash2, RotateCcw } from "lucide-react";
import { useState } from "react";

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showConfirm, setShowConfirm] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL;

  const handleClear = async () => {
    await fetch(`${API}/api/clear`, { method: "POST" });
    setShowConfirm(null);
    window.location.reload();
  };

  const handleReset = async () => {
    await fetch(`${API}/api/reset`, { method: "POST" });
    setShowConfirm(null);
    window.location.reload();
  };

  const navItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/orders", icon: Package, label: "Orders" },
    { path: "/riders", icon: Users, label: "Riders" },
    { path: "/history", icon: History, label: "History" },
    { path: "/billing", icon: Receipt, label: "Billing" },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? "w-64" : "w-20"
        } bg-slate-900 text-white transition-all duration-300 flex flex-col`}
        data-testid="sidebar"
      >
        <div className="p-6 flex items-center justify-between border-b border-slate-800">
          {sidebarOpen && (
            <h1 className="text-base font-bold tracking-tight leading-tight" data-testid="app-title">
              Thawkpalai Delivery Service
            </h1>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-slate-800 rounded-md transition-colors"
            data-testid="sidebar-toggle"
          >
            <Menu size={20} />
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-2" data-testid="main-navigation">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || (item.path === '/dashboard' && location.pathname === '/');

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all ${
                  isActive
                    ? "bg-orange-500 text-white shadow-sm"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <Icon size={20} />
                {sidebarOpen && <span className="font-medium">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 px-4 py-2">
            <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center font-bold">
              A
            </div>
            {sidebarOpen && (
              <div>
                <p className="text-sm font-medium">Admin User</p>
                <p className="text-xs text-slate-400">Administrator</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto" data-testid="main-content">
        <div className="relative">
          {/* Top-right Clear/Reset buttons */}
          <div className="absolute top-4 right-6 z-20 flex gap-2" data-testid="admin-actions">
            <button
              onClick={() => setShowConfirm("clear")}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-md hover:bg-amber-100 transition-colors"
              data-testid="clear-data-btn"
            >
              <Trash2 size={14} />
              Clear
            </button>
            <button
              onClick={() => setShowConfirm("reset")}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-50 text-red-700 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
              data-testid="reset-data-btn"
            >
              <RotateCcw size={14} />
              Reset
            </button>
          </div>

          {/* Confirmation modal */}
          {showConfirm && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" data-testid="confirm-modal-overlay">
              <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4" data-testid="confirm-modal">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {showConfirm === "clear" ? "Clear All Data?" : "Reset Everything?"}
                </h3>
                <p className="text-sm text-slate-600 mb-5">
                  {showConfirm === "clear"
                    ? "This will delete all orders and history. Riders will be kept."
                    : "This will delete everything — orders, riders, and history. Like starting fresh."}
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowConfirm(null)}
                    className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-md hover:bg-slate-200 transition-colors"
                    data-testid="confirm-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={showConfirm === "clear" ? handleClear : handleReset}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${
                      showConfirm === "clear"
                        ? "bg-amber-600 hover:bg-amber-700"
                        : "bg-red-600 hover:bg-red-700"
                    }`}
                    data-testid="confirm-action-btn"
                  >
                    {showConfirm === "clear" ? "Clear Data" : "Reset All"}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="dot-grid-bg"></div>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
